import React, { useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

// ---------------------------------------------------------------------------
// Truth-Gap Dashboard (2026-08-23 rewrite · Roger's fatigue fix)
//
// Design goals:
//   1. Never show duplicate rows.  The backend rolls up by normalized value,
//      each candidate carries a `sighting_count` badge.
//   2. One clear action per row.  Accept, Reject, or Own-Answer.
//   3. Bulk-Answer is a Focus Mode: one field at a time, big buttons,
//      progress indicator.  No more textarea grid.
//   4. Owner-authored answer is visually distinct from the proposal list -
//      a bold "Write your own" card, not a tiny eyebrow.
// ---------------------------------------------------------------------------

const STATUS_TONES = {
  MISSING: "danger",
  CONFLICTED: "warn",
  INFERRED: undefined,
  KNOWN: "success",
};

const STATUS_LABELS = {
  MISSING: "Missing",
  CONFLICTED: "Conflicted",
  INFERRED: "Inferred",
  KNOWN: "Known",
};

const CATEGORY_LABELS = {
  product_claim: "Product claim",
  audience_evidence: "Audience evidence",
  regulatory_status: "Regulatory",
  pricing: "Pricing",
  distribution: "Distribution",
  brand_voice: "Brand voice",
};

const SCOPE_LABELS = {
  brand: "Brand",
  product: "Product",
  audience: "Audience",
};

function StatusBadge({ status }) {
  return (
    <Pill tone={STATUS_TONES[status]} testId={`status-badge-${status}`}>
      {STATUS_LABELS[status] || status}
    </Pill>
  );
}

function CategoryTag({ category }) {
  if (!category) return null;
  return (
    <span
      className="bo-eyebrow"
      style={{
        fontSize: 11,
        letterSpacing: "0.06em",
        color: "var(--bo-muted)",
      }}
      data-testid={`category-tag-${category}`}
    >
      {CATEGORY_LABELS[category] || category}
    </span>
  );
}

function ValuePreview({ value }) {
  if (value == null || value === "") {
    return <em style={{ color: "var(--bo-muted)" }}>(no value)</em>;
  }
  if (Array.isArray(value)) {
    return (
      <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
        {value.slice(0, 6).map((v, i) => (
          <li key={i} style={{ marginBottom: 2 }}>
            {String(v)}
          </li>
        ))}
        {value.length > 6 && (
          <li style={{ color: "var(--bo-muted)" }}>
            …+{value.length - 6} more
          </li>
        )}
      </ul>
    );
  }
  return <div>{String(value)}</div>;
}

function SightingBadge({ count }) {
  if (!count || count <= 1) return null;
  return (
    <span
      title={`Seen on ${count} sources`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 12,
        background: "var(--bo-accent-tint, #eef4ff)",
        color: "var(--bo-accent, #234a8b)",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.02em",
      }}
      data-testid="sighting-badge"
    >
      ×{count} sightings
    </span>
  );
}

// -----------------------------------------------------------------------
// Per-field Gap row
// -----------------------------------------------------------------------

function GapRow({
  entry,
  onAcceptProposal,
  onRejectProposal,
  onOwnerAnswer,
  onAcceptTopN,
}) {
  const [expanded, setExpanded] = useState(entry.status === "CONFLICTED");
  const [answerOpen, setAnswerOpen] = useState(false);
  const [answerValue, setAnswerValue] = useState("");
  const [answerNote, setAnswerNote] = useState("");
  const [busy, setBusy] = useState(null);
  const [rowError, setRowError] = useState(null);

  const uniqueCount =
    entry.unique_candidate_count ??
    (entry.pending_proposals ? entry.pending_proposals.length : 0);
  const rawPending = entry.pending_count ?? uniqueCount;
  const collapsed = rawPending - uniqueCount;

  const submitAnswer = async (e) => {
    e.preventDefault();
    setRowError(null);
    setBusy("answer");
    try {
      const val = answerValue.trim();
      if (!val) {
        setRowError("Type your answer before saving.");
        setBusy(null);
        return;
      }
      await onOwnerAnswer({
        scope: entry.scope,
        scope_id: entry.scope_id,
        target_field_path: entry.target_field_path,
        value: val,
        note: answerNote.trim() || null,
      });
      setAnswerValue("");
      setAnswerNote("");
      setAnswerOpen(false);
    } catch (err) {
      setRowError(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const runAccept = async (pid) => {
    setRowError(null);
    setBusy(`accept:${pid}`);
    try {
      await onAcceptProposal(pid);
    } catch (err) {
      setRowError(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const runReject = async (pid) => {
    setRowError(null);
    setBusy(`reject:${pid}`);
    try {
      await onRejectProposal(pid);
    } catch (err) {
      setRowError(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const runTopN = async (n) => {
    setRowError(null);
    setBusy(`top:${n}`);
    try {
      const ids = (entry.pending_proposals || [])
        .slice(0, n)
        .map((p) => p.id)
        .filter(Boolean);
      await onAcceptTopN(ids);
    } catch (err) {
      setRowError(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const hasHighFrequency =
    (entry.pending_proposals || [])[0]?.sighting_count > 1;

  return (
    <div
      className="bo-card"
      style={{ padding: 16, marginBottom: 12 }}
      data-testid={`gap-row-${entry.target_field_path}`}
    >
      {/* Header row: status, scope, category, expand */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <StatusBadge status={entry.status} />
          <Pill tone="muted" testId={`scope-tag-${entry.scope}`}>
            {SCOPE_LABELS[entry.scope] || entry.scope}
          </Pill>
          <CategoryTag category={entry.category} />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span
            style={{ color: "var(--bo-muted)", fontSize: 12 }}
            data-testid={`pending-count-${entry.target_field_path}`}
          >
            {uniqueCount} unique candidate{uniqueCount === 1 ? "" : "s"}
            {collapsed > 0 && (
              <span style={{ marginLeft: 4 }}>
                (from {rawPending} sightings)
              </span>
            )}
          </span>
          <button
            className="bo-btn bo-btn--ghost"
            style={{ padding: "6px 10px", fontSize: 12 }}
            onClick={() => setExpanded((v) => !v)}
            data-testid={`toggle-expand-${entry.target_field_path}`}
          >
            {expanded ? "Hide details" : "Show details"}
          </button>
        </div>
      </div>

      {/* Field name + approved value */}
      <div style={{ marginTop: 10 }}>
        <div
          style={{
            fontFamily: "var(--bo-serif)",
            fontSize: 18,
            color: "var(--bo-ink)",
          }}
          data-testid={`field-path-${entry.target_field_path}`}
        >
          {entry.target_field_path}
        </div>
        {entry.approved_value != null && (
          <div
            style={{
              marginTop: 6,
              padding: 8,
              background: "var(--bo-panel-alt, #f6f4ef)",
              borderRadius: 6,
              fontSize: 14,
            }}
            data-testid={`approved-value-${entry.target_field_path}`}
          >
            <div className="bo-eyebrow" style={{ marginBottom: 4 }}>
              Approved
            </div>
            <ValuePreview value={entry.approved_value} />
          </div>
        )}
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ marginTop: 12 }}>
          {/* Top-N shortcut when we have high-frequency candidates */}
          {hasHighFrequency && uniqueCount > 3 && (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                padding: 8,
                borderRadius: 6,
                background: "var(--bo-accent-tint, #eef4ff)",
                marginBottom: 10,
              }}
            >
              <span style={{ fontSize: 12, color: "var(--bo-ink)" }}>
                Fast path:
              </span>
              <button
                className="bo-btn bo-btn--primary"
                style={{ padding: "4px 10px", fontSize: 12 }}
                disabled={busy != null}
                onClick={() => runTopN(3)}
                data-testid={`accept-top3-${entry.target_field_path}`}
              >
                {busy === "top:3"
                  ? "Approving…"
                  : "Approve top 3 most-sighted"}
              </button>
              <button
                className="bo-btn bo-btn--secondary"
                style={{ padding: "4px 10px", fontSize: 12 }}
                disabled={busy != null}
                onClick={() => runTopN(1)}
                data-testid={`accept-top1-${entry.target_field_path}`}
              >
                {busy === "top:1"
                  ? "Approving…"
                  : "Approve top 1 only"}
              </button>
            </div>
          )}

          {(entry.pending_proposals || []).length === 0 && (
            <p style={{ color: "var(--bo-muted)", fontSize: 13 }}>
              No pending candidates. Write your own answer below.
            </p>
          )}

          {/* Candidate list (ordered by sighting_count DESC from backend) */}
          {(entry.pending_proposals || []).map((p, i) => (
            <div
              key={p.id}
              style={{
                display: "flex",
                gap: 10,
                padding: "10px 8px",
                borderBottom: "1px dashed var(--bo-border, #e6e2d8)",
                alignItems: "flex-start",
              }}
              data-testid={`proposal-row-${p.id}`}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    marginBottom: 4,
                    flexWrap: "wrap",
                  }}
                >
                  {i === 0 && p.sighting_count > 1 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "var(--bo-accent, #234a8b)",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}
                    >
                      Most-sighted
                    </span>
                  )}
                  <SightingBadge count={p.sighting_count} />
                </div>
                <ValuePreview value={p.proposed_value} />
                {p.review_note && (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      color: "var(--bo-muted)",
                      fontStyle: "italic",
                    }}
                  >
                    {p.review_note}
                  </div>
                )}
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    color: "var(--bo-muted)",
                  }}
                >
                  {p.confidence_band}{" "}
                  {p.truth_gap_status ? `· ${p.truth_gap_status}` : ""}
                </div>
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 6 }}
              >
                <button
                  className="bo-btn bo-btn--primary"
                  style={{ padding: "4px 10px", fontSize: 12 }}
                  disabled={busy != null}
                  onClick={() => runAccept(p.id)}
                  data-testid={`accept-proposal-${p.id}`}
                >
                  {busy === `accept:${p.id}` ? "…" : "Approve"}
                </button>
                <button
                  className="bo-btn bo-btn--ghost"
                  style={{ padding: "4px 10px", fontSize: 12 }}
                  disabled={busy != null}
                  onClick={() => runReject(p.id)}
                  data-testid={`reject-proposal-${p.id}`}
                >
                  {busy === `reject:${p.id}` ? "…" : "Reject"}
                </button>
              </div>
            </div>
          ))}

          {/* Owner-authored answer - visually distinct card */}
          <div style={{ marginTop: 14 }}>
            {!answerOpen ? (
              <button
                className="bo-btn bo-btn--secondary"
                style={{ padding: "8px 14px", fontSize: 13 }}
                onClick={() => setAnswerOpen(true)}
                data-testid={`open-answer-${entry.target_field_path}`}
              >
                ✎ None of these are right — write your own
              </button>
            ) : (
              <form
                onSubmit={submitAnswer}
                style={{
                  padding: 14,
                  background: "var(--bo-accent-tint, #eef4ff)",
                  borderRadius: 8,
                  border: "1px solid var(--bo-accent, #234a8b)",
                }}
                data-testid={`answer-form-${entry.target_field_path}`}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    marginBottom: 8,
                    color: "var(--bo-ink)",
                  }}
                >
                  Type YOUR answer for “{entry.target_field_path}”
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--bo-muted)",
                    marginBottom: 8,
                  }}
                >
                  Enter the actual value (not the word “approve”). Example:
                  {" "}
                  <em>Transparency, Trust, Dignity</em>
                </div>
                <textarea
                  className="bo-input"
                  rows={3}
                  value={answerValue}
                  onChange={(e) => setAnswerValue(e.target.value)}
                  placeholder="Type the accurate value here…"
                  style={{ width: "100%", resize: "vertical" }}
                  data-testid={`answer-value-input-${entry.target_field_path}`}
                />
                <input
                  className="bo-input"
                  type="text"
                  value={answerNote}
                  onChange={(e) => setAnswerNote(e.target.value)}
                  placeholder="Optional note (source, context…)"
                  style={{ width: "100%", marginTop: 6 }}
                  data-testid={`answer-note-input-${entry.target_field_path}`}
                />
                <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                  <button
                    type="submit"
                    className="bo-btn bo-btn--primary"
                    disabled={busy != null || !answerValue.trim()}
                    data-testid={`submit-answer-${entry.target_field_path}`}
                  >
                    {busy === "answer" ? "Saving…" : "Save this as truth"}
                  </button>
                  <button
                    type="button"
                    className="bo-btn bo-btn--ghost"
                    onClick={() => {
                      setAnswerOpen(false);
                      setAnswerValue("");
                      setAnswerNote("");
                      setRowError(null);
                    }}
                    disabled={busy != null}
                    data-testid={`cancel-answer-${entry.target_field_path}`}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {rowError && (
            <div
              style={{
                marginTop: 10,
                color: "#8a2b0a",
                fontSize: 13,
              }}
              data-testid={`row-error-${entry.target_field_path}`}
            >
              {rowError}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Focus-Mode Bulk-Answer Modal (COMPLETE REDESIGN)
// -----------------------------------------------------------------------

function FocusModeModal({
  open,
  candidates,
  onClose,
  onOwnerAnswer,
  onAcceptTopN,
  onSkip,
}) {
  const [idx, setIdx] = useState(0);
  const [ownValue, setOwnValue] = useState("");
  const [ownNote, setOwnNote] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState([]); // ids of completed fields
  const [skipped, setSkipped] = useState([]);

  const total = candidates.length;
  const entry = candidates[idx];

  useEffect(() => {
    setOwnValue("");
    setOwnNote("");
    setError(null);
  }, [idx, open]);

  if (!open) return null;

  const nextField = () => {
    if (idx < total - 1) {
      setIdx(idx + 1);
    } else {
      // done - close after showing the finish screen
      setIdx(total); // idx == total = finish
    }
  };

  const acceptCandidate = async (p) => {
    setBusy(`accept:${p.id}`);
    setError(null);
    try {
      await onAcceptTopN([p.id]);
      setCompleted((c) => [...c, entry.target_field_path]);
      nextField();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const submitOwn = async (e) => {
    e.preventDefault();
    if (!ownValue.trim()) return;
    setBusy("own");
    setError(null);
    try {
      await onOwnerAnswer({
        scope: entry.scope,
        scope_id: entry.scope_id,
        target_field_path: entry.target_field_path,
        value: ownValue.trim(),
        note: ownNote.trim() || null,
      });
      setCompleted((c) => [...c, entry.target_field_path]);
      nextField();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const skipField = () => {
    setSkipped((s) => [...s, entry.target_field_path]);
    if (onSkip) onSkip(entry);
    nextField();
  };

  const progressPct = total === 0 ? 0 : Math.round((idx / total) * 100);
  const isFinished = idx >= total || total === 0;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(20, 22, 28, 0.55)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      data-testid="focus-mode-overlay"
    >
      <div
        className="bo-card"
        style={{
          width: "min(880px, 100%)",
          maxHeight: "90vh",
          padding: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header + progress */}
        <div
          style={{
            padding: "16px 24px 12px 24px",
            borderBottom: "1px solid var(--bo-border, #e6e2d8)",
            background: "var(--bo-panel-alt, #f6f4ef)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div>
              <div className="bo-eyebrow">Focus Mode · Truth-Gap</div>
              <div
                style={{
                  fontFamily: "var(--bo-serif)",
                  fontSize: 20,
                  marginTop: 2,
                }}
              >
                {isFinished
                  ? "You’re done."
                  : `Field ${idx + 1} of ${total}`}
              </div>
            </div>
            <button
              className="bo-btn bo-btn--ghost"
              onClick={onClose}
              disabled={busy != null}
              data-testid="focus-mode-close"
              style={{ padding: "6px 12px" }}
            >
              {isFinished ? "Close" : "Save & exit"}
            </button>
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 3,
              background: "var(--bo-border, #e6e2d8)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${progressPct}%`,
                height: "100%",
                background: "var(--bo-accent, #234a8b)",
                transition: "width 200ms",
              }}
              data-testid="focus-mode-progress"
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 8,
              fontSize: 12,
              color: "var(--bo-muted)",
            }}
          >
            <span data-testid="focus-mode-approved-count">
              ✓ {completed.length} approved
            </span>
            <span data-testid="focus-mode-skipped-count">
              ↷ {skipped.length} skipped
            </span>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
          {isFinished ? (
            <div
              style={{ textAlign: "center", padding: "40px 20px" }}
              data-testid="focus-mode-finished"
            >
              <div style={{ fontSize: 48 }}>✓</div>
              <div
                style={{
                  fontFamily: "var(--bo-serif)",
                  fontSize: 24,
                  marginTop: 10,
                }}
              >
                Every field reviewed.
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "var(--bo-muted)",
                  marginTop: 8,
                }}
              >
                {completed.length} approved, {skipped.length} skipped.
              </div>
              <button
                className="bo-btn bo-btn--primary"
                style={{ marginTop: 20, padding: "10px 20px" }}
                onClick={onClose}
                data-testid="focus-mode-done"
              >
                Return to dashboard
              </button>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: 6,
                }}
              >
                <StatusBadge status={entry.status} />
                <Pill tone="muted">
                  {SCOPE_LABELS[entry.scope] || entry.scope}
                </Pill>
                <CategoryTag category={entry.category} />
              </div>
              <div
                style={{
                  fontFamily: "var(--bo-serif)",
                  fontSize: 22,
                  marginBottom: 4,
                }}
                data-testid="focus-mode-field-path"
              >
                {entry.target_field_path}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--bo-muted)",
                  marginBottom: 20,
                }}
              >
                {entry.unique_candidate_count ??
                  (entry.pending_proposals || []).length}{" "}
                unique candidate
                {(entry.unique_candidate_count ?? 0) === 1 ? "" : "s"}
              </div>

              {/* Suggested values (top 5 by sighting_count) */}
              {(entry.pending_proposals || []).length > 0 && (
                <>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      marginBottom: 8,
                    }}
                  >
                    Pick one of these suggestions:
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {(entry.pending_proposals || [])
                      .slice(0, 5)
                      .map((p, i) => (
                        <button
                          key={p.id}
                          className="bo-card"
                          type="button"
                          onClick={() => acceptCandidate(p)}
                          disabled={busy != null}
                          data-testid={`focus-mode-suggestion-${i}`}
                          style={{
                            padding: 12,
                            textAlign: "left",
                            cursor: busy != null ? "wait" : "pointer",
                            borderColor:
                              i === 0
                                ? "var(--bo-accent, #234a8b)"
                                : "var(--bo-border, #e6e2d8)",
                            borderWidth: i === 0 ? 2 : 1,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 10,
                              marginBottom: 6,
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                              }}
                            >
                              {i === 0 && (
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "var(--bo-accent, #234a8b)",
                                    letterSpacing: "0.04em",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Recommended
                                </span>
                              )}
                              <SightingBadge count={p.sighting_count} />
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "var(--bo-muted)",
                                }}
                              >
                                {p.confidence_band}
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "var(--bo-accent, #234a8b)",
                              }}
                            >
                              {busy === `accept:${p.id}`
                                ? "Approving…"
                                : "Approve →"}
                            </span>
                          </div>
                          <ValuePreview value={p.proposed_value} />
                          {p.review_note && (
                            <div
                              style={{
                                marginTop: 4,
                                fontSize: 12,
                                color: "var(--bo-muted)",
                                fontStyle: "italic",
                              }}
                            >
                              {p.review_note}
                            </div>
                          )}
                        </button>
                      ))}
                  </div>
                </>
              )}

              {/* Divider */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  margin: "24px 0 16px 0",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: "var(--bo-border, #e6e2d8)",
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--bo-muted)",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Or write your own
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: "var(--bo-border, #e6e2d8)",
                  }}
                />
              </div>

              <form onSubmit={submitOwn}>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--bo-muted)",
                    marginBottom: 6,
                  }}
                >
                  Type the actual value (not the word “approve”).
                </div>
                <textarea
                  className="bo-input"
                  rows={3}
                  value={ownValue}
                  onChange={(e) => setOwnValue(e.target.value)}
                  placeholder={`Type your answer for "${entry.target_field_path}"…`}
                  style={{ width: "100%", resize: "vertical" }}
                  data-testid="focus-mode-own-value"
                  disabled={busy != null}
                />
                <input
                  className="bo-input"
                  type="text"
                  value={ownNote}
                  onChange={(e) => setOwnNote(e.target.value)}
                  placeholder="Optional note (source, context…)"
                  style={{ width: "100%", marginTop: 6 }}
                  data-testid="focus-mode-own-note"
                  disabled={busy != null}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 12,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                  }}
                >
                  <button
                    type="button"
                    className="bo-btn bo-btn--ghost"
                    onClick={skipField}
                    disabled={busy != null}
                    data-testid="focus-mode-skip"
                    style={{ padding: "8px 14px" }}
                  >
                    Skip this field
                  </button>
                  <button
                    type="submit"
                    className="bo-btn bo-btn--primary"
                    disabled={busy != null || !ownValue.trim()}
                    data-testid="focus-mode-save"
                    style={{ padding: "8px 20px" }}
                  >
                    {busy === "own" ? "Saving…" : "Save & next →"}
                  </button>
                </div>
              </form>

              {error && (
                <div
                  style={{
                    marginTop: 12,
                    color: "#8a2b0a",
                    fontSize: 13,
                  }}
                  data-testid="focus-mode-error"
                >
                  {error}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Main Page
// -----------------------------------------------------------------------

export default function TruthGapDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    scope: null,
    scope_id: null,
    status: null,
    include_known: false,
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.scope) params.set("scope", filters.scope);
      if (filters.scope_id) params.set("scope_id", filters.scope_id);
      if (filters.include_known) params.set("include_known", "true");
      const r = await api.get(`/genesis/truth-gap?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.scope, filters.scope_id, filters.include_known]);

  const onAcceptProposal = async (pid) => {
    await api.post(`/genesis/proposals/${pid}/accept`);
    await load();
  };

  const onRejectProposal = async (pid) => {
    await api.post(`/genesis/proposals/${pid}/reject`);
    await load();
  };

  const onOwnerAnswer = async (req) => {
    await api.post(`/genesis/answer`, req);
    await load();
  };

  const onAcceptTopN = async (ids) => {
    if (!ids || ids.length === 0) return;
    await api.post(`/genesis/proposals/bulk-accept`, {
      proposal_ids: ids,
      authority: "APPROVED",
      note: "Approved from Truth-Gap Dashboard",
    });
    await load();
  };

  // Focus-mode state
  const [focusOpen, setFocusOpen] = useState(false);
  const focusCandidates = useMemo(() => {
    if (!data) return [];
    return data.entries.filter(
      (e) => e.status === "MISSING" || e.status === "CONFLICTED"
    );
  }, [data]);

  const filteredEntries = useMemo(() => {
    if (!data) return [];
    if (!filters.status) return data.entries;
    return data.entries.filter((e) => e.status === filters.status);
  }, [data, filters.status]);

  const summary = data?.summary || {};

  return (
    <Shell>
      <div className="bo-page" data-testid="truth-gap-dashboard">
        <div style={{ marginBottom: 16 }}>
          <div className="bo-eyebrow">Genesis · M3</div>
          <h1
            style={{
              fontFamily: "var(--bo-serif)",
              fontSize: 28,
              margin: "4px 0 4px 0",
            }}
          >
            Truth-Gap Dashboard
          </h1>
          <p
            style={{
              color: "var(--bo-muted)",
              margin: 0,
              fontSize: 14,
            }}
          >
            Approve suggested answers, reject wrong ones, or type your own.
            Duplicates are collapsed — each row shows how many sources
            observed each value.
          </p>
        </div>

        {/* Summary strip */}
        <div
          className="bo-card"
          style={{ padding: 16, marginBottom: 12 }}
          data-testid="truth-gap-summary"
        >
          <div
            style={{
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <SummaryStat
                label="Missing"
                value={summary.missing}
                tone="danger"
              />
              <SummaryStat
                label="Conflicted"
                value={summary.conflicted}
                tone="warn"
              />
              <SummaryStat label="Inferred" value={summary.inferred} />
              <SummaryStat
                label="Known"
                value={summary.known}
                tone="success"
              />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className="bo-btn bo-btn--primary"
                onClick={() => setFocusOpen(true)}
                disabled={focusCandidates.length === 0}
                data-testid="open-focus-mode"
                style={{ padding: "10px 18px" }}
              >
                ⚡ Focus Mode ({focusCandidates.length} to review)
              </button>
              <button
                className="bo-btn bo-btn--ghost"
                onClick={load}
                disabled={loading}
                data-testid="refresh-truth-gap"
              >
                {loading ? "Loading…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div
          className="bo-card"
          style={{ padding: 12, marginBottom: 12 }}
          data-testid="truth-gap-filters"
        >
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <FilterButton
              label="All"
              active={filters.status == null}
              onClick={() => setFilters((f) => ({ ...f, status: null }))}
            />
            {["MISSING", "CONFLICTED", "INFERRED", "KNOWN"].map((s) => (
              <FilterButton
                key={s}
                label={STATUS_LABELS[s]}
                active={filters.status === s}
                onClick={() => setFilters((f) => ({ ...f, status: s }))}
              />
            ))}
            <label
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                gap: 6,
                alignItems: "center",
                fontSize: 13,
              }}
            >
              <input
                type="checkbox"
                checked={filters.include_known}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    include_known: e.target.checked,
                  }))
                }
                data-testid="include-known-toggle"
              />
              Show approved fields too
            </label>
          </div>
        </div>

        {error && <ErrorBanner error={error} testId="truth-gap-error" />}

        {loading && <LoadingCard />}

        {!loading && data && filteredEntries.length === 0 && (
          <EmptyState
            title="Nothing to review"
            description="Every field is either approved or waiting on a fresh crawl."
            testId="truth-gap-empty"
          />
        )}

        {!loading &&
          filteredEntries.map((e) => (
            <GapRow
              key={`${e.scope}:${e.scope_id}:${e.target_field_path}`}
              entry={e}
              onAcceptProposal={onAcceptProposal}
              onRejectProposal={onRejectProposal}
              onOwnerAnswer={onOwnerAnswer}
              onAcceptTopN={onAcceptTopN}
            />
          ))}

        <FocusModeModal
          open={focusOpen}
          candidates={focusCandidates}
          onClose={() => {
            setFocusOpen(false);
            load();
          }}
          onOwnerAnswer={onOwnerAnswer}
          onAcceptTopN={onAcceptTopN}
        />
      </div>
    </Shell>
  );
}

function SummaryStat({ label, value, tone }) {
  const color =
    tone === "danger"
      ? "#8a2b0a"
      : tone === "warn"
      ? "#8a6a0a"
      : tone === "success"
      ? "#1f6c3f"
      : "var(--bo-ink)";
  return (
    <div>
      <div
        className="bo-eyebrow"
        style={{ fontSize: 11, color: "var(--bo-muted)" }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--bo-serif)",
          fontSize: 24,
          color,
          fontWeight: 600,
        }}
        data-testid={`summary-${label.toLowerCase()}`}
      >
        {value ?? 0}
      </div>
    </div>
  );
}

function FilterButton({ label, active, onClick }) {
  return (
    <button
      className={`bo-btn ${active ? "bo-btn--primary" : "bo-btn--ghost"}`}
      style={{ padding: "6px 14px", fontSize: 13 }}
      onClick={onClick}
      data-testid={`filter-${label.toLowerCase()}`}
    >
      {label}
    </button>
  );
}
